import React, { useState, useEffect } from 'react';
import { 
  getBanners, 
  fetchBannersFromApi, 
  addBannerToApi, 
  toggleBannerInApi, 
  deleteBannerFromApi, 
  BannerData 
} from '../../utils/bannerStorage';
import './Dashboard.css';

const BannerManagement: React.FC = () => {
  const [banners, setBanners] = useState<BannerData[]>(getBanners());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [image, setImage] = useState('');
  const [title, setTitle] = useState('');
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const list = await fetchBannersFromApi();
    setBanners(list);
  };

  const handleOpenAddModal = () => {
    setImage('');
    setTitle('');
    setUploadError('');
    setIsModalOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 8 * 1024 * 1024) {
      setUploadError('Image size should be less than 8MB');
      return;
    }

    setUploadError('');
    setTitle(file.name.replace(/\.[^/.]+$/, ''));
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        setImage(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSaveBanner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) {
      alert('Please upload an image file or provide an image URL');
      return;
    }

    setIsSaving(true);
    try {
      const updatedList = await addBannerToApi({
        title: title || `Banner ${banners.length + 1}`,
        image
      });
      setBanners(updatedList);
      setIsModalOpen(false);
      alert('New banner uploaded to Server Database successfully! It is now LIVE globally on all devices.');
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save banner to server.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteBanner = async (id: number | string) => {
    if (window.confirm('Are you sure you want to delete this banner image globally?')) {
      const updatedList = await deleteBannerFromApi(id);
      setBanners(updatedList);
    }
  };

  const handleToggleStatus = async (id: number | string, currentActiveStatus: boolean = true) => {
    const updatedList = await toggleBannerInApi(id, currentActiveStatus);
    setBanners(updatedList);
  };

  return (
    <div className="banner-management-page">
      
      {/* Header Bar */}
      <div className="module-header-bar">
        <div className="module-header-info">
          <div className="module-header-icon icon-purple">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
          </div>
          <div>
            <h2 className="module-header-title">Banner Management (Global Server Sync)</h2>
            <p className="module-header-subtitle">Upload hero banner slides displayed on the main website homepage across all desktop & mobile browsers.</p>
          </div>
        </div>
        <button 
          onClick={handleOpenAddModal}
          className="module-action-btn"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <span>Upload Banner Image</span>
        </button>
      </div>

      {/* Banner Image Cards List */}
      <div className="banners-grid">
        {banners.map((b, idx) => (
          <div key={b.id} className="banner-card">
            
            {/* Image Preview Box */}
            <div className="banner-preview-box">
              <img 
                src={b.image} 
                alt={b.title || `Banner ${idx + 1}`} 
              />
              <span className={`banner-badge ${b.isActive !== false ? 'active' : 'disabled'}`}>
                <span className="status-dot-indicator"></span>
                <span>{b.isActive !== false ? 'Active (Live)' : 'Paused'}</span>
              </span>
            </div>

            {/* Actions Footer */}
            <div className="banner-card-footer">
              <span className="banner-title-label" title={b.title || `Banner #${idx + 1}`}>
                {b.title || `Banner #${idx + 1}`}
              </span>

              <div className="banner-actions-group">
                <button 
                  onClick={() => handleToggleStatus(b.id, b.isActive !== false)}
                  className={b.isActive !== false ? 'banner-action-pause' : 'banner-action-enable'}
                >
                  {b.isActive !== false ? 'Pause' : 'Enable'}
                </button>

                <button 
                  onClick={() => handleDeleteBanner(b.id)}
                  className="banner-action-delete"
                  title="Delete Banner Image"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                  <span>Delete</span>
                </button>
              </div>
            </div>

          </div>
        ))}
      </div>

      {/* Upload Modal */}
      {isModalOpen && (
        <div className="dashboard-modal-backdrop">
          <div className="dashboard-modal-box">
            
            {/* Modal Header */}
            <div className="modal-head">
              <div className="modal-head-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <h3>Upload New Banner Image</h3>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="modal-close-btn">×</button>
            </div>

            <form onSubmit={handleSaveBanner}>
              
              {/* File Upload Box */}
              <div className="dropzone-modal-box" style={{ textAlign: 'center' }}>
                <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', marginBottom: '6px' }}>
                  Select Banner Image File
                </label>
                
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileChange} 
                  style={{ margin: '0 auto 8px auto', display: 'block', fontSize: '0.78rem' }}
                />

                {uploadError && <p style={{ color: '#dc2626', fontSize: '0.78rem', margin: '4px 0' }}>{uploadError}</p>}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>OR URL:</span>
                  <input 
                    type="text"
                    value={image}
                    onChange={(e) => setImage(e.target.value)}
                    placeholder="/Website-Images/Banners/Ertiga.png or https://..."
                    style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                  />
                </div>

                {/* Preview Thumbnail */}
                {image && (
                  <div style={{ marginTop: '10px', height: '140px', background: '#ffffff', borderRadius: '6px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    <img src={image} alt="Preview" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                  </div>
                )}
              </div>

              {/* Modal Buttons */}
              <div className="modal-foot">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isSaving}
                  className="btn-modal-cancel"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={isSaving}
                  className="btn-modal-primary"
                  style={{ opacity: isSaving ? 0.75 : 1 }}
                >
                  {isSaving ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="spinning">
                        <circle cx="12" cy="12" r="10" strokeOpacity="0.25"></circle>
                        <path d="M12 2a10 10 0 0 1 10 10"></path>
                      </svg>
                      <span>Syncing...</span>
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                        <polyline points="7 3 7 8 15 8"></polyline>
                      </svg>
                      <span>Publish Live Banner</span>
                    </>
                  )}
                </button>
              </div>

            </form>

          </div>
        </div>
      )}

    </div>
  );
};

export default BannerManagement;
